import * as React from 'react'
import { Route, Redirect, Switch, useLocation } from 'react-router-dom'
import CookieConsent from 'react-cookie-consent'
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl'

import { uiKitMiddleware } from '@dooer/react-ui-kit'

import 'normalize.css/normalize.css'
import './css/main.scss'
import './css/academy.scss'
import styles from './css/cookie.module.scss'

import Layout from './components/layout'
import Button from './components/button'
import ErrorBoundary from './components/error-boundary'
import Navigation from './components/navigation'
import { onLeavePage } from './leave-detector'

import AboutView from './views/about'
import NotFoundView from './views/404'
import AcademyView from './views/academy'
import CareerView from './views/career'
import BillingView from './views/free'
import PayrollView from './views/free-payroll'
import FreeStartView from './views/free-start'
import Feedback from './views/feedback'
import StartView from './views/start'
import AcceptOfferView from './views/accept-offer'
import BankSyncView from './views/sync-bank'
import IntegrityView from './views/gdpr'
import TosView from './views/tos'
import CookiesView from './views/cookies'
import LPConsultantView from './views/lp/consultant'
import LPSignupView from './views/lp/signup'
import PressView from './views/press'
import PressViewAssets from './views/press-assets'
import Screenshots from './views/screenshots/index.js'
import PartnerView from './views/partner'
import ContactPopup from './components/design/contact-popup'
import LandingPage from './views/landing-page'

const url = window.location.href
const isLocal = /localhost:5555/.test(url)

const UiKitProvider = uiKitMiddleware()

export default function App() {
  const { pathname } = useLocation()
  const [popup, togglePopup] = React.useState(false)

  const showedContactPopup = sessionStorage.getItem('retention-popup-viewed') === 'true'
  const hideShowContactPopup = () => {
    sessionStorage.setItem('retention-popup-viewed', 'true')
    togglePopup(false)
  }

  React.useEffect(() => {
    onLeavePage(() => togglePopup(true))
  }, [])

  return (
    <UiKitProvider>
      <ErrorBoundary>
        <Layout>
          <Route component={Navigation} path="/" />

          {!showedContactPopup && popup && <ContactPopup onClose={() => hideShowContactPopup()} />}

          <React.Suspense fallback={<div />}>
            <Switch>
              <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />
              <Route component={LandingPage} exact path="/" />
              <Route component={FreeStartView} path="/bokforing" />
              <Route component={StartView} path="/byra" />
              <Route component={PayrollView} path="/lon" />
              <Route component={BillingView} path="/fakturering" />
              <Redirect from="/gratis" to="/" />
              <Route component={Feedback} path="/feedback/:id/:score" />
              <Route component={Feedback} path="/feedback/:id" />
              <Route component={() => <BillingView variant={2} />} path="/gratis-v2" />
              <Redirect from="/karriar/:id" to="/karriar" />
              <Redirect from="/jobb" to="/karriar" />
              <Route component={CareerView} path="/karriar" />
              <Redirect from="/omoss" to="/om-oss" />
              <Route component={AboutView} path="/om-oss" />
              <Route component={AcademyView} path="/hjalp/*" />
              <Route component={AcademyView} path="/hjalp/" />
              <Route component={AcademyView} path="/hjalp" />
              <Route component={() => <Redirect to="/signup" />} path="/pris" />
              <Redirect from="/join" to="/signup" />
              <Redirect from="/signup" to="/byra" />
              <Route component={AcceptOfferView} path="/offert/:organizationId/:id" />
              <Route component={BankSyncView} path="/synka/:shortid" />
              <Redirect from="/faq" to="/vara-tjanster" />
              <Redirect from="/bokforing" to="/vara-tjanster" />
              <Redirect from="/vara-tjanster" to="/byra" />
              <Redirect from="/integritetspolicy" to="/gdpr" />
              <Route component={IntegrityView} path="/gdpr" />
              <Redirect from="/allmannavillkor" to="/tos" />
              <Route component={TosView} path="/tos" />
              <Route component={CookiesView} path="/cookies" />
              <Route component={LPConsultantView} path="/lp/konsult" />
              <Route component={LPSignupView} path="/lp/signup" />
              <Route component={PressView} path="/press" />
              <Route component={PressViewAssets} path="/assets/:type" />
              <Route component={PartnerView} path="/partner" />
              {isLocal && <Route component={Screenshots} path="/dev/screenshots" />}
              <Route component={NotFoundView} />
            </Switch>
          </React.Suspense>
        </Layout>

        <CookieConsent
          buttonClasses={styles.accept}
          ButtonComponent={Button}
          buttonText={<FormattedMessage defaultMessage="Jag accepterar" id="app.cookie-consent.accept" />}
          buttonWrapperClasses={styles.buttonWrapper}
          containerClasses={styles.root}
          contentClasses={styles.content}
          declineButtonClasses={styles.decline}
          declineButtonText={
            <FormattedMessage defaultMessage="Jag gillar inte kakor" id="app.cookie-consent.decline" />
          }
          disableButtonStyles
          enableDeclineButton
          flipButtons
          onAccept={() => window.location.reload()}
        >
          <FormattedHTMLMessage
            defaultMessage={`Vi använder cookies för att göra ditt besök hos oss bättre. Acceptera vår <a href="/cookies" class="{linkClass}">cookie policy</a> så får du den bästa upplevelsen av dooer.com<br /> 🍪 😀 👍🏻 👍🏻`}
            id="app.cookie-consent.text"
            values={{
              linkClass: styles.readmore,
            }}
          />
        </CookieConsent>
      </ErrorBoundary>
    </UiKitProvider>
  )
}
